/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { Popover } from '@base-ui/react/popover';

import { DatePickerTrigger } from './DatePickerTrigger';
import type { DatePickerShell } from './DatePicker.types';
import type {
    FlexDatePickerPopoverAlign,
    FlexDatePickerSize,
} from './FlexDatePicker.types';

import sharedStyles from './sharedStyles.module.scss';
import styles from './DatePickerPopover.module.scss';

interface DatePickerPopoverProps {
    shell: DatePickerShell;
    /** The picker body rendered inside the popup. */
    children: React.ReactNode;
    isDisabled?: boolean;
    clickTagCategory?: string;
    size?: FlexDatePickerSize;
    popoverAlign?: FlexDatePickerPopoverAlign;
    /** Accessible name of the popup. */
    title?: string;
    /** Screen-reader prefix that names the trigger. */
    triggerLabel?: string;
    /** Accessible name of the trigger's clear button. */
    clearLabel?: string;
}

export function DatePickerPopover({
    shell,
    children,
    isDisabled,
    clickTagCategory,
    size,
    popoverAlign = 'center',
    title = 'Choose Dates',
    triggerLabel,
    clearLabel,
}: DatePickerPopoverProps) {
    const { isOpen, onOpenChange } = shell;

    return (
        <Popover.Root modal="trap-focus" open={isOpen} onOpenChange={onOpenChange}>
            <DatePickerTrigger
                TriggerPart={Popover.Trigger}
                shell={shell}
                isDisabled={isDisabled}
                clickTagCategory={clickTagCategory}
                size={size}
                label={triggerLabel}
                clearLabel={clearLabel}
            />
            <Popover.Portal>
                <Popover.Backdrop className={styles.Backdrop} />
                <Popover.Positioner
                    className={styles.Positioner}
                    sideOffset={4}
                    align={popoverAlign}
                    side="bottom"
                    // Always stay below the trigger. By default a popup too tall for
                    // either side falls back to the left or right of the trigger;
                    // instead it's capped to the available height and scrolls.
                    collisionAvoidance={{ side: 'none', fallbackAxisSide: 'none' }}
                >
                    <Popover.Popup
                        className={cx(sharedStyles.Popup, styles.Popup)}
                        aria-label={title}
                    >
                        {children}
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    );
}
